import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TbArrowLeft } from "react-icons/tb";
import moment from "moment";
import AxiosInstance from "../../services/Api";
import styles from "../masters/RequestDemoView.module.css";

const LIST_PATH = "/dashboard/appversion";

const isSuccess = (response) =>
  response?.data?.status === true || response?.data?.success === true;

const formatViewDate = (value) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "—";
};

const getOsType = (item) => {
  if (item?.os_type) return item.os_type;
  if (item?.android_version != null && item.android_version !== "") return "Android";
  if (item?.ios_version) return "iOS";
  return "—";
};

const getVersion = (item) => {
  if (item?.version != null && String(item.version).trim() !== "") {
    return String(item.version).trim();
  }
  if (item?.android_version != null && item.android_version !== "") {
    return item.android_version;
  }
  return item.ios_version ?? "—";
};

const getStatusLabel = (item) => {
  if (!item) return "—";
  const raw = item.status ?? item.version_status;
  if (raw === 1 || raw === "1" || String(raw).toLowerCase() === "active") {
    return "Active";
  }
  if (raw === 0 || raw === "0" || String(raw).toLowerCase() === "inactive") {
    return "Inactive";
  }
  return "Inactive";
};

function InfoField({ label, value }) {
  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.fieldValue}>{value ?? "—"}</div>
    </div>
  );
}

function AppVersionView() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDetails = useCallback(async () => {
    if (!id) {
      setError("No app version ID provided.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await AxiosInstance.post("/app-version/view", {
        id: Number(id),
      });
      const data =
        response?.data?.app_version ||
        response?.data?.appVersion ||
        response?.data?.data;

      if (isSuccess(response) && data) {
        setItem(data);
      } else {
        throw new Error(response?.data?.message || "Failed to fetch app version");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const forceUpdateLabel =
    item?.force_update === true || item?.force_update === 1 || item?.force_update === "1"
      ? "Yes"
      : "No";

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={() => navigate(LIST_PATH)}
        >
          <TbArrowLeft size={18} />
          Back to List
        </button>
        <h1 className={styles.title}>APP VERSION DETAILS</h1>
      </div>

      {loading ? <p>Loading...</p> : null}
      {error ? <p>{error}</p> : null}

      {!loading && !error && item ? (
        <>
          <div className={styles.detailsGrid}>
            <InfoField label="Platform" value={getOsType(item)} />
            <InfoField label="Version" value={getVersion(item)} />
            <InfoField
              label="Build Type"
              value={item.build_type || item.type}
            />
            <InfoField
              label="Submitted Date"
              value={formatViewDate(item.submitted_date || item.created_date)}
            />
            <InfoField
              label="Released Date"
              value={formatViewDate(item.released_date)}
            />
            <InfoField label="Status" value={getStatusLabel(item)} />
            <InfoField label="Force Update" value={forceUpdateLabel} />
          </div>
          <div style={{ marginTop: 24, display: "flex", gap: 12 }}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate(`/dashboard/appversion/edit/${item.id}`)}
            >
              Edit
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

export default AppVersionView;
